import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  Paper,
  Grid,
  Chip,
  TextField,
  Button,
  Divider,
  Alert,
} from '@mui/material';
import {
  ReceiptLong,
  Save,
  Comment,
  AccountBalanceWallet,
  Person,
  Phone,
  Email,
  LocationOn,
} from '@mui/icons-material';
import { InputAdornment } from '@mui/material';
import Euro from '@mui/icons-material/Euro';

const ReservationOrderTab = ({
  commande,
  apiUrl,
  token,
  reservation,
  onOrderUpdated,
}) => {
  // Fonction de normalisation robuste pour extraire la commande
  const extractCommandData = (raw) => {
    if (!raw) return null;
    let cmd = Array.isArray(raw) ? raw[0] : raw;
    if (cmd.result && Array.isArray(cmd.result)) cmd = cmd.result[0];
    else if (cmd.data && Array.isArray(cmd.data)) cmd = cmd.data[0];
    else if (cmd.data) cmd = cmd.data;

    if (!cmd || !cmd.id_cmd) return null;

    // Calcul automatique du TTC via les détails si les champs globaux sont à 0 ou absents
    let details = cmd.details_commandes || cmd.details || [];
    let fallbackTtc = details.reduce((sum, det) => {
      return sum + (Number(det.pu_offre_detcmd || det.prix_u || 0) * Number(det.qnt_offre_detcmd || det.quantite || 1));
    }, 0);

    const ttc = Number(cmd.total_ttc_cmd ?? cmd.total_ttc ?? cmd.montant_ttc ?? fallbackTtc);
    const htc = Number(cmd.total_htc_cmd ?? cmd.total_htc ?? cmd.montant_ht ?? (ttc / 1.2));
    const tva = Number(cmd.total_tva_cmd ?? cmd.total_tva ?? cmd.montant_tva ?? (ttc - htc));

    return {
      ...cmd,
      total_htc_cmd: htc,
      total_tva_cmd: tva,
      total_ttc_cmd: ttc,
      prix_libre:
        cmd.prix_libre !== undefined && cmd.prix_libre !== null
          ? cmd.prix_libre
          : '',
      prix_special_soa: Number(cmd.prix_special_soa ?? 0),
      montant_soa_du_au_praticien: Number(
        cmd.montant_soa_du_au_praticien ?? cmd.calcul_soa_praticien ?? 0
      ),
      taux_commission: Number(cmd.taux_commission ?? 20),
      cible_cmd: cmd.cible_cmd ?? '',
      etat_cmd: cmd.etat_cmd ?? 'SUCCESS',
      ref_cmd: cmd.ref_cmd ?? '',
      details_commandes: details,
    };
  };

  const [currentCmd, setCurrentCmd] = useState(() => extractCommandData(commande));

  // Extraction élargie et sécurisée des données du client
  const clientData =
    currentCmd?.client ||
    commande?.client ||
    reservation?.client ||
    reservation?.originalData?.client ||
    reservation?.originalData?.user?.client ||
    null;

  // Détection fiable de is_patient
  const rawIsPatient = clientData?.is_patient;
  const isPatient =
    rawIsPatient === 1 ||
    rawIsPatient === '1' ||
    rawIsPatient === true;

  const roleUser = Number(
    reservation?.originalData?.user?.role_user ??
      reservation?.user?.role_user ??
      reservation?.originalData?.role_user ??
      4
  );

  const rolePrefix = roleUser === 4 ? 'praticien' : 'admin';

  const [prixLibre, setPrixLibre] = useState('');
  const [commentCmd, setCommentCmd] = useState('');
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  // Synchroniser l'état local si la prop commande change
  useEffect(() => {
    if (commande) {
      const normalized = extractCommandData(commande);
      setCurrentCmd(normalized);
      setPrixLibre(
        normalized.prix_libre !== null && normalized.prix_libre !== undefined
          ? normalized.prix_libre
          : ''
      );
      setCommentCmd(normalized.comment_cmd ?? '');
    }
  }, [commande]);

  // Calculs dynamiques basés sur currentCmd
  const calculatedTtc = Number(currentCmd?.total_ttc_cmd || 0);
  const calculatedHtc = Number(currentCmd?.total_htc_cmd || calculatedTtc / 1.2);
  const calculatedTva = Number(currentCmd?.total_tva_cmd || calculatedTtc - calculatedHtc);

  const calculatedSoaPraticien = (() => {
    const specialSoa = Number(currentCmd?.prix_special_soa || 0);
    const don = isPatient && prixLibre !== '' && prixLibre !== null ? Number(prixLibre) : 0;

    if (specialSoa > 0) {
      const reste = specialSoa - don;
      return reste > 0 ? reste : 0;
    }
    return currentCmd?.montant_soa_du_au_praticien ?? 0;
  })();

  if (!currentCmd) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: '18px',
          bgcolor: '#FFFFFF',
          border: '1px solid #E2E8F0',
          textAlign: 'center',
        }}
      >
        <ReceiptLong sx={{ fontSize: 48, color: '#94A3B8', mb: 1.5 }} />
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: '#1E293B', mb: 1 }}
        >
          Aucune commande associée
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Il n'y a pas de facture ou de commande enregistrée pour cette
          réservation.
        </Typography>
      </Paper>
    );
  }

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFeedback({ type: '', message: '' });

    try {
      const endpoint = `${apiUrl}/${rolePrefix}/commands/${currentCmd.id_cmd}/prix-libre`;

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
        body: JSON.stringify({
          prix_libre: isPatient ? (prixLibre === '' ? null : Number(prixLibre)) : null,
          comment_cmd: commentCmd,
        }),
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error(
          `Erreur serveur (${response.status}) : La route est introuvable ou renvoie du HTML.`
        );
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.message || 'Erreur lors de la mise à jour de la commande.'
        );
      }

      const rawUpdated = result?.data || result?.result || result;

      // Fusion sécurisée pour conserver les détails de commande existants si l'API ne les renvoie pas
      const mergedCmd = {
        ...currentCmd,
        ...rawUpdated,
        details_commandes:
          rawUpdated.details_commandes && rawUpdated.details_commandes.length > 0
            ? rawUpdated.details_commandes
            : currentCmd.details_commandes,
      };

      const updatedData = extractCommandData(mergedCmd);
      setCurrentCmd(updatedData);
      setPrixLibre(
        updatedData.prix_libre !== null && updatedData.prix_libre !== undefined
          ? updatedData.prix_libre
          : ''
      );
      setCommentCmd(updatedData.comment_cmd ?? '');

      setFeedback({
        type: 'success',
        message: 'Commande et calculs SOA mis à jour avec succès !',
      });

      if (onOrderUpdated) {
        onOrderUpdated(updatedData);
      }
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err.message || 'Une erreur est survenue.',
      });
    } finally {
      setLoading(false);
    }
  };

  const isPatientOffer = currentCmd.cible_cmd === 'patient_soa';

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, md: 4 },
        borderRadius: '20px',
        bgcolor: '#FFFFFF',
        border: '1px solid #E2E8F0',
        boxShadow: '0 10px 30px rgba(0,0,0,0.03)',
      }}
    >
      {/* En-tête */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3.5,
          pb: 2,
          borderBottom: '1px solid #F1F5F9',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              bgcolor: '#EEF2FF',
              color: '#4F46E5',
              p: 1.5,
              borderRadius: '14px',
              display: 'flex',
            }}
          >
            <ReceiptLong />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: '#0F172A', fontSize: '1.15rem' }}
            >
              Détails de la Commande & Facturation SOA
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#64748B',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 0.5,
              }}
            >
              Référence :{' '}
              <strong style={{ color: '#1E293B' }}>{currentCmd.ref_cmd}</strong>
              <Chip
                label={`Rôle : ${rolePrefix}`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  bgcolor: '#E0F2FE',
                  color: '#0369A1',
                  fontWeight: 700,
                  textTransform: 'capitalize',
                }}
              />
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {currentCmd.cible_cmd && (
            <Chip
              label={`Cible : ${currentCmd.cible_cmd}`}
              size="small"
              sx={{
                fontWeight: 700,
                bgcolor: isPatientOffer ? '#F3E8FF' : '#F1F5F9',
                color: isPatientOffer ? '#7E22CE' : '#475569',
                px: 1,
              }}
            />
          )}
          <Chip
            label={currentCmd.etat_cmd || 'N/C'}
            color={currentCmd.etat_cmd === 'SUCCESS' ? 'success' : 'warning'}
            size="small"
            sx={{ fontWeight: 800, px: 1 }}
          />
        </Stack>
      </Box>

      {/* Bloc Profil du Client / Patient */}
      {clientData && (
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3.5,
            bgcolor: isPatient ? '#F5F3FF' : '#F8FAFC',
            borderRadius: '14px',
            border: `1px solid ${isPatient ? '#DDD6FE' : '#E2E8F0'}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ bgcolor: isPatient ? '#EDE9FE' : '#E2E8F0', color: isPatient ? '#7C3AED' : '#475569', p: 1, borderRadius: '10px', display: 'flex' }}>
                <Person fontSize="small" />
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E293B' }}>
                {clientData.prenom_client || clientData.prenom || ''} {clientData.nom_client || clientData.nom || ''}
              </Typography>
            </Box>
            <Chip
              label={isPatient ? 'Patient SOA' : 'Client Standard'}
              size="small"
              sx={{
                fontWeight: 800,
                bgcolor: isPatient ? '#7C3AED' : '#E2E8F0',
                color: isPatient ? '#FFFFFF' : '#334155',
                fontSize: '0.7rem',
              }}
            />
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" sx={{ color: '#64748B', display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}>
                <Email sx={{ fontSize: 14 }} /> Email
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B', mt: 0.2 }}>
                {clientData.email_client || clientData.email || 'Non renseigné'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" sx={{ color: '#64748B', display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}>
                <Phone sx={{ fontSize: 14 }} /> Téléphone
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B', mt: 0.2 }}>
                {clientData.phone_client || clientData.telephone || clientData.phone || 'Non renseigné'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" sx={{ color: '#64748B', display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}>
                <LocationOn sx={{ fontSize: 14 }} /> Localisation
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B', mt: 0.2 }}>
                {[clientData.addresse_client || clientData.adresse, clientData.ville_client || clientData.ville].filter(Boolean).join(', ') || 'Non renseignée'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Détails des lignes de commande */}
      {currentCmd.details_commandes &&
        currentCmd.details_commandes.length > 0 && (
          <Box
            sx={{
              mb: 3.5,
              p: 2.5,
              bgcolor: '#F8FAFC',
              borderRadius: '14px',
              border: '1px solid #E2E8F0',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#64748B',
                fontWeight: 800,
                display: 'block',
                mb: 1,
                letterSpacing: '0.5px',
              }}
            >
              PRESTATION SÉLECTIONNÉE
            </Typography>
            {currentCmd.details_commandes.map((det, index) => (
              <Box
                key={det.id_detcmd || index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: '#1E293B' }}
                >
                  {det.label_offre_detcmd || det.libelle || 'Prestation'}{' '}
                  <span style={{ color: '#64748B', fontWeight: 400 }}>
                    (Qté: {det.qnt_offre_detcmd || det.quantite || 1})
                  </span>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 800, color: '#4F46E5' }}
                >
                  {Number(det.pu_offre_detcmd || det.prix_u || 0).toFixed(2)} €
                </Typography>
              </Box>
            ))}
          </Box>
        )}

      {/* Grille des montants financiers globaux */}
      <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
        <Grid item xs={12} sm={6} md={isPatient ? 3 : 4}>
          <Box
            sx={{
              p: 2.5,
              bgcolor: '#F8FAFC',
              borderRadius: '14px',
              border: '1px solid #E2E8F0',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: '#64748B', fontWeight: 700, mb: 0.5 }}
            >
              TOTAL HT
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>
              {calculatedHtc.toFixed(2)} €
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={isPatient ? 3 : 4}>
          <Box
            sx={{
              p: 2.5,
              bgcolor: '#F8FAFC',
              borderRadius: '14px',
              border: '1px solid #E2E8F0',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: '#64748B', fontWeight: 700, mb: 0.5 }}
            >
              TVA
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A' }}>
              {calculatedTva.toFixed(2)} €
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={isPatient ? 3 : 4}>
          <Box
            sx={{
              p: 2.5,
              bgcolor: '#EFF6FF',
              borderRadius: '14px',
              border: '1px solid #BFDBFE',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: '#1D4ED8', fontWeight: 700, mb: 0.5 }}
            >
              TOTAL TTC
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 900, color: '#1E40AF' }}>
              {calculatedTtc.toFixed(2)} €
            </Typography>
          </Box>
        </Grid>

        {isPatient && (
          <Grid item xs={12} sm={6} md={3}>
            <Box
              sx={{
                p: 2.5,
                bgcolor: '#F0FDF4',
                borderRadius: '14px',
                border: '1px solid #BBF7D0',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: '#166534', fontWeight: 700, mb: 0.5 }}
              >
                PRIX LIBRE (DON)
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#15803D' }}>
                {prixLibre !== '' && prixLibre !== null && prixLibre !== undefined
                  ? `${Number(prixLibre).toFixed(2)} €`
                  : 'Non défini'}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Synthèse Financière & Gestion Praticien */}
      {isPatient && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            bgcolor: '#F8FAFC',
            borderRadius: '16px',
            border: '1px solid #E2E8F0',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 800,
              color: '#1E293B',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              fontSize: '0.95rem',
            }}
          >
            <Box
              sx={{
                bgcolor: '#E0E7FF',
                color: '#4F46E5',
                p: 0.8,
                borderRadius: '8px',
                display: 'flex',
              }}
            >
              <AccountBalanceWallet fontSize="small" />
            </Box>
            Synthèse Financière & Gestion Praticien
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: '#FFFFFF',
                  borderRadius: '12px',
                  border: '1px solid #E2E8F0',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#64748B',
                    fontWeight: 700,
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  Prix Spécial SOA
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 800, color: '#0F172A' }}
                >
                  {currentCmd.prix_special_soa
                    ? `${Number(currentCmd.prix_special_soa).toFixed(2)} €`
                    : 'N/A'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: '#ECFDF5',
                  borderRadius: '12px',
                  border: '1px solid #A7F3D0',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#065F46',
                    fontWeight: 700,
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  Montant SOA Dû au Praticien
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 900, color: '#059669' }}
                >
                  {calculatedSoaPraticien.toFixed(2)} €
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Formulaire de mise à jour */}
      {isPatient && (
        <>
          <Divider sx={{ my: 3.5, borderColor: '#F1F5F9' }} />

          <Box component="form" onSubmit={handleUpdate}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 800, color: '#1E293B', mb: 2 }}
            >
              Modification Post-Consultation (Prix Libre & Commentaire)
            </Typography>

            {feedback.message && (
              <Alert
                severity={feedback.type}
                sx={{ mb: 2.5, borderRadius: '12px', fontWeight: 600 }}
              >
                {feedback.message}
              </Alert>
            )}

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Prix Libre / Don du Patient (€)"
                  type="number"
                  size="small"
                  fullWidth
                  value={prixLibre}
                  onChange={(e) => setPrixLibre(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Euro sx={{ color: '#64748B', fontSize: 18, mr: 0.5 }} />
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{ step: '0.01', min: '0' }}
                  sx={{
                    bgcolor: '#F8FAFC',
                    borderRadius: '12px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      fontWeight: 700,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Commentaire de la commande"
                  fullWidth
                  size="small"
                  value={commentCmd}
                  onChange={(e) => setCommentCmd(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Comment sx={{ color: '#64748B', fontSize: 18, mr: 0.5 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    bgcolor: '#F8FAFC',
                    borderRadius: '12px',
                    '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                  }}
                />
              </Grid>
              
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={<Save />}
                  sx={{
                    bgcolor: '#4F46E5',
                    fontWeight: 700,
                    textTransform: 'none',
                    px: 4,
                    py: 1.5,
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
                    '&:hover': { bgcolor: '#4338CA' },
                  }}
                >
                  {loading ? 'Enregistrement...' : 'Mettre à jour la commande'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default ReservationOrderTab;